import React, { Fragment, useState } from 'react';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import capitalize from 'lodash/capitalize';
import find from 'lodash/find';
import { useTranslation } from 'react-i18next';
import path from 'path';
import { connect } from 'react-redux';
import Cookies from 'js-cookie';
import { useLastLocation } from 'react-router-last-location';
import { useLocation } from 'react-router-dom';

import { Cell, Grid } from 'formlabs-web-components/Grid';
import { DisplayComponent } from 'formlabs-web-components/BaseComponents';
import { Header } from 'formlabs-web-components/Type';
import { Link } from 'formlabs-web-components/Link';
import { Spinner } from 'formlabs-web-components/Navigation';
import CenterModal from '../../../../common/components/modal/CenterModal';

import { updateLocaleAsync as updateLocaleAction } from '../../../../common/ducks/i18n';
import { getCookieDomain } from '../../../../common/utils';

import './CountrySelection.module.css';

const CountrySelection = ({ countries, updateLocale, activeRegion }) => {
  const location = useLocation();
  const [t] = useTranslation();
  const lastLocation = useLastLocation();
  const [loading, setLoading] = useState(false);

  countries = orderBy(countries, ['continent', 'name'], ['desc', 'asc']);
  const continents = groupBy(countries, country => country.continent);

  const pathname = location
    ? location.pathname
    : lastLocation
    ? lastLocation.pathname
    : '';

  const activeCountry = find(countries, { code: activeRegion.toUpperCase() });
  const onStorePage =
    activeCountry.storePage && pathname.indexOf(activeCountry.storePage) !== -1;

  const lastLocationCleaned = pathname
    .replace(/\/[a-z]{0,2}\//g, '')
    .replace(/\/blog\/.*/g, '/blog/')
    .replace(/\/company\/press\/.*/g, '/company/press/')
    .replace(/\/roi\/.*/g, '');

  if (loading) {
    return (
      <Grid direction="y">
        <Cell>
          <Header level={1} displayAs={2} weight={600}>
            {t('Switching Country')}
          </Header>
        </Cell>
        <Cell>
          <Spinner size={8} />
        </Cell>
      </Grid>
    );
  }

  return (
    <div>
      <Header level={1} displayAs={2} weight={600}>
        {t('Choose your Country')}
      </Header>
      {Object.keys(continents).map(continent => (
        <section key={continent} styleName="continent">
          {/* Keep for translation parser:
            t('continent: AS')
            t('continent: EU')
            t('continent: NA')
            t('continent: SA')
          */}
          <Header key={continent} level={2} displayAs={3} weight={400}>
            {t(`continent: ${continent}`)}
          </Header>
          <Grid
            direction="x"
            gutters="padding"
            addStyleNames="small-up-1 medium-up-3 large-up-5"
          >
            {continents[continent].map(country => {
              const { language, currencyCode, storePage } = country;
              const region = country.code.toLowerCase();
              const to =
                onStorePage && storePage
                  ? storePage
                  : (
                      country.path +
                      (country.path !== '/tr/' &&
                      country.path !== '/cn/' &&
                      country.path !== '/jp/'
                        ? lastLocationCleaned
                        : '')
                    ).replace('//', '/');

              return (
                <Cell
                  key={`country-${country.code}`}
                  backgroundColor={
                    activeCountry.code === country.code ? 'light-blue' : null
                  }
                >
                  <Link
                    onClick={async () => {
                      // FIXME workaround until we have a switch cart API
                      const desiredStoreId = `${region}_${language}`;
                      const storeId = Cookies.get('store_view');
                      if (storeId && desiredStoreId !== storeId) {
                        Cookies.remove('store_view', {
                          domain: getCookieDomain()
                        });
                        Cookies.remove('cart', { domain: getCookieDomain() });
                      }

                      setLoading(true);

                      await updateLocale(language, region, currencyCode);
                      window.location = to;
                    }}
                  >
                    <div styleName="item">
                      <img
                        src={path.join(
                          process.env.PUBLIC_PATH,
                          `flags/${country.code.toLowerCase()}.svg`
                        )}
                        alt={country.name}
                      />
                      <div styleName="text">
                        <span styleName="country">{country.name}</span>
                        <span>
                          {capitalize(country.languageName.split(',')[0])}
                        </span>
                      </div>
                    </div>
                  </Link>
                </Cell>
              );
            })}
          </Grid>
        </section>
      ))}
    </div>
  );
};

const CountrySelectionComponent = connect(
  state => {
    return {
      activeLanguage: state.i18n.language,
      activeRegion: state.i18n.region
    };
  },
  dispatch => {
    return {
      updateLocale: (language, region, currency) => {
        dispatch(updateLocaleAction(language, region, currency));
      }
    };
  }
)(CountrySelection);

const CountrySelectionModal = ({ children, countries }) => {
  const [isShowing, setShowing] = useState(false);

  const show = () => {
    setShowing(true);
  };

  const hide = () => {
    setShowing(false);
  };

  return (
    <Fragment>
      <div
        styleName="modal-button"
        onClick={show}
        onKeyUp={show}
        role="button"
        tabIndex="-1"
      >
        {children}
      </div>
      {isShowing ? (
        <CenterModal width="1200px" onClose={hide}>
          <CountrySelectionComponent countries={countries} hideModal={hide} />
        </CenterModal>
      ) : null}
    </Fragment>
  );
};

export { CountrySelectionModal };

export default DisplayComponent(CountrySelectionComponent);
